import { Button, Form, Input, Modal, Select, Space } from 'antd';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import { IModal } from 'common/models/model.interface';
import configurationStore from 'common/stores/configuration/configuration.store';
import { notification } from 'common/utils/notification';
import { toGateOptions } from 'features/eiq-secure/constans/common.eiq-secure';
import { observer } from 'mobx-react-lite';
import React, { FC, useCallback, useEffect } from 'react';
import SelectState from 'common/components/form-elements/select-state/select-state';
import { IQRCodeAdmitResidentModalProps } from './eiq-secure-qr-code-admit-modal.types';
import guestService from 'common/services/guest-service/guest.service';
import { EntryType } from 'common/enums/entry-type.enum';
import AlertBlock from 'common/components/info-block/alert-block/alert-block';
import featureStore from 'common/stores/features/feature.store';
import plateRecognitionContext from 'features/eiq-secure/stores/license-plate-recognition-context';
import { useBoundPlateRecognition } from 'features/eiq-secure/hooks/use-bound-plate-recognition/use-bound-plate-recognition.hook';
import successSound from 'assets/sounds/success.wav';
import './eiq-secure-qr-code-scanner.scss';
import { eiqSecureResidentLicenseValidation } from '../eiq-secure-resident-license/eiq-secure-resident-license.validation';
import TextArea from 'antd/es/input/TextArea';
import { residentEntryTypeOptions } from '../eiq-secure-resident-license/eiq-secure-resident-license';
import { ReactComponent as AdmitIcon } from 'assets/icons/admit.svg';

const QRCodeAdmitResidentModal: FC<IModal<IQRCodeAdmitResidentModalProps, any>> = observer(({ isOpen, onClose, initData }) => {
  const [form] = Form.useForm();
  const gateOptions = toGateOptions(configurationStore?.configuration?.gates || []);
  const defaultLicenseState = configurationStore.configuration?.default.defaultLicenseState;
  const entryTypeValue = Form.useWatch('entryType', form);

  const playSound = (sound: any) => {
    const audio = new Audio(sound);
    audio.play();
  };

  useBoundPlateRecognition(() => {
    form.setFieldValue('carLicensePlate', initData?.carLicensePlate ?? '');
    form.setFieldValue('carState', initData?.carLicenseState ?? configurationStore.configuration?.default.defaultLicenseState);
    form.setFieldValue('gate', initData?.gate ?? undefined);
  });

  const getFormInitData = (data?: IQRCodeAdmitResidentModalProps) => {
    let carLicenseState =
      data?.carLicenseState ?? plateRecognitionContext.data?.licenseState ?? configurationStore.configuration?.default.defaultLicenseState;

    let carLicenseNumber = data?.carLicensePlate ?? plateRecognitionContext.data?.licensePlate;

    const result = {
      addressId: data?.addressId,
      residentId: data?.residentId,
      gate: data?.gate,
      carExpirationDate: data?.carExpirationDate,
      carLicensePlate: carLicenseNumber,
      carState: carLicenseState,
      deviceId: data?.deviceId,
      entryType: carLicenseNumber ? EntryType.Driver : EntryType.Pedestrian,
    };

    return result;
  };

  useEffect(() => {
    if (form && isOpen && initData) {
      form.setFieldsValue(getFormInitData(initData));
    }
  }, [isOpen, form, initData]);

  const hideModal = (...rest: any) => {
    form.resetFields();
    if (onClose) {
      onClose(...rest);
    }
  };

  const onFinish = (values: any) => {
    if (!initData) {
      return;
    }

    const { residentId, addressId, carExpirationDate, deviceId, isEIQPassportUsed } = initData;
    const plateRecognitionId = plateRecognitionContext.getRecognitionId(values.gate, values.carLicensePlate, values.carState);

    const admitData: any = {
      gate: values.gate,
      entryType: values.entryType,
      carState: values.carState,
      carLicensePlate: values.carLicensePlate,
      carExpirationDate: carExpirationDate,
      deviceId: deviceId,
      isEIQPassportUsed: isEIQPassportUsed,
      driverCompanyName: values.entryType === EntryType.Passenger ? values.driverCompanyName : null,
      driverName: values.entryType === EntryType.Passenger ? `${values.driverFirstName ?? ''} ${values.driverLastName ?? ''}`.trim() : null,
      note: values.note ? values.note : null,
      plateRecognitionId: plateRecognitionId,
    };

    return guestService.admitResident(residentId, addressId, admitData).then((result) => {
      notification.destroy();
      notification.success({ message: 'The resident  has been admitted' });
      plateRecognitionContext.clear();
      playSound(successSound);
      hideModal();
    });
  };

  const onEntryTypeChanged = (newValue: EntryType) => {
    if (newValue !== EntryType.Driver) {
      form.setFieldValue('carLicensePlate', '');
      form.setFieldValue('carState', defaultLicenseState);
    } else {
      form.setFieldValue('carLicensePlate', initData?.carLicensePlate);
      form.setFieldValue('carState', initData?.carLicenseState);
    }
  };

  const getTitle = () => {
    if (!initData) {
      return 'Admit';
    }

    const { resident, address } = initData;

    return (
      <div className="qr-code-admit-modal-title-resident">
        <div>
          <div className="title">Approve Resident</div>
        </div>
        <div className="qr-code-admit-modal-title-resident-info">
          <div>{resident}</div>
          <div>{address}</div>
        </div>
      </div>
    );
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      form.submit();
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown, true);
    } else {
      document.removeEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen]);

  return (
    <Modal
      centered
      width={500}
      open={isOpen}
      wrapClassName="custom-modal custom-modal--so"
      title={getTitle()}
      closable={true}
      onCancel={hideModal}>
      <div className="qr-alert-wrapper">
        {featureStore.isFastAccessAlertsEnabled && initData?.addressAlertMessage && (
          <AlertBlock alertHeader="Address alert" alertMessage={initData?.addressAlertMessage} />
        )}
        {featureStore.isFastAccessAlertsEnabled && initData?.residentAlertMessage && (
          <AlertBlock alertHeader="Resident alert" alertMessage={initData?.residentAlertMessage} />
        )}
      </div>

      <FormWrapper form={form} onFinish={onFinish} layout="vertical" initialValues={getFormInitData(initData)}>
        <div className="container-row">
          <Form.Item name="entryType" label="Entry Type">
            <Select
              options={residentEntryTypeOptions}
              placeholder="Select Entry Type"
              popupClassName="so-select"
              onChange={onEntryTypeChanged}
            />
          </Form.Item>
          {entryTypeValue === EntryType.Passenger && (
            <>
              <Form.Item name="driverFirstName" label="Driver first name">
                <Input placeholder="Enter driver first name" autoFocus />
              </Form.Item>
              <Form.Item name="driverLastName" label="Driver last name">
                <Input placeholder="Enter driver last name" />
              </Form.Item>
              <Form.Item name="driverCompanyName" label="Driver company name" rules={eiqSecureResidentLicenseValidation.driverCompanyName}>
                <Input placeholder="Enter driver company name" />
              </Form.Item>
            </>
          )}
          {entryTypeValue === EntryType.Driver && (
            <>
              <Form.Item name="carLicensePlate" label="License Plate" rules={eiqSecureResidentLicenseValidation.carLicensePlate}>
                <Input placeholder="Enter license plate" autoFocus={isOpen} />
              </Form.Item>

              <Form.Item name="carState" label="License state" rules={eiqSecureResidentLicenseValidation.carState}>
                <SelectState placeholder="Select a license state" popupClassName="so-select" />
              </Form.Item>
            </>
          )}
          {entryTypeValue === EntryType.Other && (
            <Form.Item name="note" label="Notes">
              <TextArea placeholder="Enter notes" maxLength={500} style={{ resize: 'none' }} autoFocus />
            </Form.Item>
          )}
          <Form.Item name="gate" label="Location (Gate)" rules={eiqSecureResidentLicenseValidation.gate}>
            <Select options={gateOptions} placeholder="Select Location (Gate)" popupClassName="so-select" />
          </Form.Item>
          <Space className="footer">
            <Button className="eiq-button" type="link" onClick={hideModal}>
              Cancel
            </Button>
            <Button htmlType="submit" type="primary" className="primary-btn eiq-button" icon={<AdmitIcon />}>
              Approve
            </Button>
          </Space>
        </div>
      </FormWrapper>
    </Modal>
  );
});

export default React.memo(QRCodeAdmitResidentModal, (prevProps, nextProps) => {
  return prevProps.isOpen === nextProps.isOpen;
});
